import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useDrag } from 'react-dnd'
import { Edit3, Trash2, Target, Users, MessageSquare, Activity, Clock, Link as LinkIcon, ExternalLink, Play, Search, X } from 'lucide-react'
import { TileType } from './TilePalette'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import usePlayStore from '../../stores/playStore'

const getTileIcon = (type) => {
  switch (type) {
    case TileType.DRILL:
      return Target
    case TileType.SCRIMMAGE:
      return Users
    case TileType.SPEAKING_POINTS:
      return MessageSquare
    case TileType.FITNESS:
      return Activity
    default:
      return Target
  }
}

const getTileColorClasses = (type) => {
  switch (type) {
    case TileType.DRILL:
      return {
        border: 'border-green-300',
        borderEdit: 'border-green-400',
        icon: 'text-green-600',
        bg: 'bg-green-50'
      }
    case TileType.SCRIMMAGE:
      return {
        border: 'border-blue-300',
        borderEdit: 'border-blue-400',
        icon: 'text-blue-600',
        bg: 'bg-blue-50'
      }
    case TileType.SPEAKING_POINTS:
      return {
        border: 'border-purple-300',
        borderEdit: 'border-purple-400',
        icon: 'text-purple-600',
        bg: 'bg-purple-50'
      }
    case TileType.FITNESS:
      return {
        border: 'border-orange-300',
        borderEdit: 'border-orange-400',
        icon: 'text-orange-600',
        bg: 'bg-orange-50'
      }
    default:
      return {
        border: 'border-gray-300',
        borderEdit: 'border-gray-400',
        icon: 'text-gray-600',
        bg: 'bg-gray-50'
      }
  }
}

const PracticeTile = ({ tile, onUpdate, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editedTile, setEditedTile] = useState(tile)
  const [showPlayModal, setShowPlayModal] = useState(false)
  const [savedPlays, setSavedPlays] = useState([])
  const [publicPlays, setPublicPlays] = useState([])
  const [isLoadingPlays, setIsLoadingPlays] = useState(false)
  const [isLoadingPublicPlays, setIsLoadingPublicPlays] = useState(false)
  const [playSearch, setPlaySearch] = useState('')
  const [playView, setPlayView] = useState('my') // 'my' or 'public'
  
  const { getSavedPlays, getPublicPlays } = usePlayStore()

  // Update editedTile when tile prop changes
  useEffect(() => {
    setEditedTile({
      ...tile,
      newLink: '', // Initialize newLink field for adding links
      // Ensure links is an array (migrate from old 'link' property)
      links: tile.links || (tile.link ? [tile.link] : null)
    })
  }, [tile])

  // Load plays when modal opens or view changes
  useEffect(() => {
    if (showPlayModal) {
      if (playView === 'my') {
        loadMyPlays()
      } else if (playView === 'public') {
        loadPublicPlays()
      }
    }
  }, [showPlayModal, playView])

  const loadMyPlays = async () => {
    setIsLoadingPlays(true)
    try {
      const plays = await getSavedPlays()
      // Filter to only plays that have a GIF URL
      const playsWithGifs = plays.filter(play => play.gifUrl && play.gifStatus === 'completed')
      setSavedPlays(playsWithGifs)
    } catch (error) {
      console.error('Error loading plays:', error)
      setSavedPlays([])
    } finally {
      setIsLoadingPlays(false)
    }
  }

  const loadPublicPlays = async () => {
    setIsLoadingPublicPlays(true)
    try {
      const plays = await getPublicPlays()
      // Filter to only plays that have a GIF URL
      const playsWithGifs = plays.filter(play => play.gifUrl && play.gifStatus === 'completed')
      setPublicPlays(playsWithGifs)
    } catch (error) {
      console.error('Error loading public plays:', error)
      setPublicPlays([])
    } finally {
      setIsLoadingPublicPlays(false)
    }
  }

  const filterPlays = (plays, searchTerm) => {
    if (!searchTerm.trim()) {
      return plays
    }
    const searchLower = searchTerm.toLowerCase().trim()
    return plays.filter(play => {
      // Search in name
      if (play.name?.toLowerCase().includes(searchLower)) {
        return true
      }
      // Search in description
      if (play.description?.toLowerCase().includes(searchLower)) {
        return true
      }
      // Search in sport type
      if (play.sportType?.toLowerCase().includes(searchLower)) {
        return true
      }
      // Search in tags
      if (play.tags && Array.isArray(play.tags)) {
        const tagMatch = play.tags.some(tag => 
          tag.toLowerCase().includes(searchLower)
        )
        if (tagMatch) {
          return true
        }
      }
      return false
    })
  }

  const handleLinkPlay = (play) => {
    setEditedTile({
      ...editedTile,
      playGifUrl: play.gifUrl,
      playName: play.name,
      playId: play.id
    })
    setShowPlayModal(false)
    setPlaySearch('')
  }

  const handleUnlinkPlay = () => {
    setEditedTile({
      ...editedTile,
      playGifUrl: null,
      playName: null,
      playId: null
    })
  }

  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'practice-tile',
    item: { tileId: tile.id, tileType: tile.type },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  }))

  const Icon = getTileIcon(tile.type)
  const colorClasses = getTileColorClasses(tile.type)

  const handleSave = () => {
    // Clean up newLink field before saving (it's just for UI)
    const { newLink, ...tileToSave } = editedTile
    // Ensure links is null if empty array
    if (tileToSave.links && tileToSave.links.length === 0) {
      tileToSave.links = null
    }
    onUpdate(tileToSave)
    setIsEditing(false)
  }

  const handleCancel = () => {
    setEditedTile(tile)
    setIsEditing(false)
  }

  const handleDurationChange = (value) => {
    const numValue = parseInt(value) || 10
    const duration = Math.max(1, Math.min(180, numValue)) // Min 1 minute, max 180 minutes
    setEditedTile({
      ...editedTile,
      duration
    })
  }

  return (
    <>
      {isEditing ? (
      <div className={`bg-white border-2 ${colorClasses.borderEdit} rounded-lg p-4 mb-3 shadow-md`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <Icon size={20} className={colorClasses.icon} />
            <input
              type="text"
              value={editedTile.title}
              onChange={(e) => setEditedTile({ ...editedTile, title: e.target.value })}
              className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Tile title"
            />
          </div>
        </div>

        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-700 mb-1">Duration (min)</label>
          <input
            type="number"
            min="1"
            max="180"
            value={editedTile.duration || 10}
            onChange={(e) => handleDurationChange(e.target.value)}
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <p className="text-xs text-gray-500 mt-1">Start and end times are automatically calculated</p>
        </div>

        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
          <div className="border border-gray-300 rounded">
            <ReactQuill
              value={editedTile.description || ''}
              onChange={(value) => setEditedTile({ ...editedTile, description: value })}
              placeholder="Add description..."
              modules={{
                toolbar: [
                  ['bold', 'italic'],
                  [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                  ['clean']
                ]
              }}
              formats={['bold', 'italic', 'list', 'bullet']}
              theme="snow"
              style={{
                fontSize: '14px'
              }}
            />
          </div>
        </div>

        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-700 mb-1">External Links (optional)</label>
          
          {/* Display existing links */}
          {editedTile.links && editedTile.links.length > 0 && (
            <div className="space-y-2 mb-2">
              {editedTile.links.map((link, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-blue-50 border border-blue-200 rounded">
                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                    <LinkIcon size={14} className="text-blue-600 flex-shrink-0" />
                    <a
                      href={link.startsWith('http://') || link.startsWith('https://') ? link : `https://${link}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-xs text-blue-600 hover:text-blue-800 hover:underline truncate"
                      title={link}
                    >
                      {link}
                    </a>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      const newLinks = editedTile.links.filter((_, i) => i !== index)
                      setEditedTile({ ...editedTile, links: newLinks.length > 0 ? newLinks : null })
                    }}
                    className="p-1 text-red-500 hover:text-red-700 rounded flex-shrink-0 ml-2"
                    title="Remove link"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
          
          {/* Add new link input */}
          <div className="flex items-center space-x-2">
            <LinkIcon size={14} className="text-gray-400" />
            <input
              type="url"
              value={editedTile.newLink || ''}
              onChange={(e) => setEditedTile({ ...editedTile, newLink: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && editedTile.newLink?.trim()) {
                  e.preventDefault()
                  const linkValue = editedTile.newLink.trim()
                  const currentLinks = editedTile.links || []
                  if (!currentLinks.includes(linkValue)) {
                    setEditedTile({
                      ...editedTile,
                      links: [...currentLinks, linkValue],
                      newLink: ''
                    })
                  }
                }
              }}
              className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="https://example.com (Press Enter to add)"
            />
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                if (editedTile.newLink?.trim()) {
                  const linkValue = editedTile.newLink.trim()
                  const currentLinks = editedTile.links || []
                  if (!currentLinks.includes(linkValue)) {
                    setEditedTile({
                      ...editedTile,
                      links: [...currentLinks, linkValue],
                      newLink: ''
                    })
                  }
                }
              }}
              disabled={!editedTile.newLink?.trim()}
              className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add
            </button>
          </div>
        </div>

        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-700 mb-1">Linked Sportigee Play</label>
          {editedTile.playGifUrl ? (
            <div className="flex items-center justify-between p-2 bg-blue-50 border border-blue-200 rounded">
              <div className="flex items-center space-x-2 flex-1">
                <Play size={14} className="text-blue-600" />
                <span className="text-xs text-gray-700">{editedTile.playName || 'Linked Sportigee Play'}</span>
              </div>
              <button
                onClick={handleUnlinkPlay}
                className="p-1 text-red-500 hover:text-red-700 rounded"
                title="Unlink play"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                setShowPlayModal(true)
              }}
              className="w-full px-3 py-2 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors flex items-center justify-center space-x-2"
            >
              <Play size={14} />
              <span>Link Play</span>
            </button>
          )}
        </div>

        <div className="flex space-x-2">
          <button
            onClick={handleSave}
            className="flex-1 px-3 py-1.5 text-sm bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
          >
            Save
          </button>
          <button
            onClick={handleCancel}
            className="flex-1 px-3 py-1.5 text-sm bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
      ) : (
    <div
      ref={drag}
      className={`bg-white border-2 ${colorClasses.border} rounded-lg p-4 shadow-sm hover:shadow-md transition-all cursor-move ${
        isDragging ? 'opacity-50' : ''
      }`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center space-x-2 flex-1">
          <Icon size={20} className={colorClasses.icon} />
          <h3 className="text-sm font-semibold text-gray-900">{tile.title || 'Untitled'}</h3>
        </div>
        <div className="flex space-x-1">
          <button
            onClick={(e) => {
              e.stopPropagation()
              setIsEditing(true)
            }}
            className="p-1 text-gray-500 hover:text-blue-600 rounded"
            title="Edit"
          >
            <Edit3 size={14} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              onDelete(tile.id)
            }}
            className="p-1 text-gray-500 hover:text-red-600 rounded"
            title="Delete"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {tile.description && (
        <div 
          className="text-xs text-gray-600 mb-2 max-w-none practice-tile-description"
          style={{
            lineHeight: '1.5'
          }}
          dangerouslySetInnerHTML={{ __html: tile.description }}
        />
      )}

      {tile.playGifUrl && (
        <div className="mb-2">
          <a
            href={tile.playGifUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center space-x-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
          >
            <Play size={12} />
            <span>View Play: {tile.playName || 'Linked Sportigee Play'}</span>
            <ExternalLink size={10} />
          </a>
        </div>
      )}

      {(tile.links && tile.links.length > 0) || tile.link ? (
        <div className="space-y-2 mb-2">
          {(tile.links || (tile.link ? [tile.link] : [])).map((link, index) => {
            // Ensure link has protocol
            const linkUrl = link.startsWith('http://') || link.startsWith('https://') 
              ? link 
              : `https://${link}`
            
            return (
              <div key={index} className="flex items-center justify-between p-2 bg-blue-50 border border-blue-200 rounded">
                <div className="flex items-center space-x-2 flex-1 min-w-0">
                  <LinkIcon size={14} className="text-blue-600 flex-shrink-0" />
                  <a
                    href={linkUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-xs text-blue-600 hover:text-blue-800 hover:underline truncate"
                    title={link}
                  >
                    {link}
                  </a>
                </div>
              </div>
            )
          })}
        </div>
      ) : null}

      <div className="flex items-center space-x-1 text-xs text-gray-500">
        <Clock size={12} />
        <span>
          {tile.startTime || 0}-{tile.endTime || 0} minutes
        </span>
      </div>

    </div>
      )}

      {/* Play Selection Modal - Rendered via Portal (available in both edit and view modes) */}
      {showPlayModal && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" 
          onClick={() => {
            setShowPlayModal(false)
            setPlaySearch('')
            setPlayView('my')
          }}
        >
          <div className="bg-white rounded-lg p-6 w-96 max-w-full mx-4 max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">Link a Play</h3>
            
            {/* Tab Navigation */}
            <div className="flex space-x-2 mb-4 border-b border-gray-200">
              <button
                onClick={() => {
                  setPlayView('my')
                  setPlaySearch('')
                }}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  playView === 'my'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                My Plays
              </button>
              <button
                onClick={() => {
                  setPlayView('public')
                  setPlaySearch('')
                }}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  playView === 'public'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Public Plays
              </button>
            </div>

            {/* My Plays View */}
            {playView === 'my' && (
              <div className="space-y-2 flex-1 flex flex-col min-h-0">
                {/* Search Input */}
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    type="text"
                    value={playSearch}
                    onChange={(e) => setPlaySearch(e.target.value)}
                    placeholder="Search by name, description, tags, or sport type..."
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div className="flex-1 overflow-y-auto space-y-2">
                  {isLoadingPlays ? (
                    <p className="text-gray-500 text-center py-4">Loading plays...</p>
                  ) : (() => {
                    const filteredPlays = filterPlays(savedPlays, playSearch)
                    return filteredPlays.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">
                        {playSearch.trim() ? 'No plays match your search' : 'No plays with GIFs available. Save a play first to generate a GIF.'}
                      </p>
                    ) : (
                      filteredPlays.map((play) => (
                        <div
                          key={play.id}
                          onClick={() => handleLinkPlay(play)}
                          className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                        >
                          <div className="font-medium flex items-center space-x-2">
                            <span>{play.name}</span>
                            {play.isPublic && (
                              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                                Public
                              </span>
                            )}
                            {play.sportType && (
                              <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded capitalize">
                                {play.sportType}
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">{play.description}</div>
                          {play.tags && play.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {play.tags.map((tag) => (
                                <span
                                  key={tag}
                                  className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                          <div className="text-xs text-gray-400 mt-1">
                            {new Date(play.updatedAt).toLocaleString()}
                          </div>
                        </div>
                      ))
                    )
                  })()}
                </div>
              </div>
            )}

            {/* Public Plays View */}
            {playView === 'public' && (
              <div className="space-y-2 flex-1 flex flex-col min-h-0">
                {/* Search Input */}
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    type="text"
                    value={playSearch}
                    onChange={(e) => setPlaySearch(e.target.value)}
                    placeholder="Search by name, description, tags, or sport type..."
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div className="flex-1 overflow-y-auto space-y-2">
                  {isLoadingPublicPlays ? (
                    <p className="text-gray-500 text-center py-4">Loading public plays...</p>
                  ) : (() => {
                    const filteredPlays = filterPlays(publicPlays, playSearch)
                    return filteredPlays.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">
                        {playSearch.trim() ? 'No plays match your search' : 'No public plays with GIFs available'}
                      </p>
                    ) : (
                      filteredPlays.map((play) => (
                        <div
                          key={play.id}
                          onClick={() => handleLinkPlay(play)}
                          className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                        >
                          <div className="font-medium flex items-center space-x-2">
                            <span>{play.name}</span>
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                              Public
                            </span>
                            {play.sportType && (
                              <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded capitalize">
                                {play.sportType}
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">{play.description}</div>
                          {play.tags && play.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {play.tags.map((tag) => (
                                <span
                                  key={tag}
                                  className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                          <div className="text-xs text-gray-400 mt-1">
                            {new Date(play.updatedAt).toLocaleString()}
                          </div>
                        </div>
                      ))
                    )
                  })()}
                </div>
              </div>
            )}

            <div className="flex justify-end mt-6">
              <button
                onClick={() => {
                  setShowPlayModal(false)
                  setPlaySearch('')
                  setPlayView('my')
                }}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  )
}

export default PracticeTile

